import { useCallback } from 'react'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'

import useAppDispatch from '../../../store/hooks/useAppDispatch'
import useAppSelector from '../../../store/hooks/useAppSelector'
import {
  checkoutFiltersOrderFormatSelector,
  checkoutFiltersServiceTimeSelector,
} from '../../checkout/store/checkoutSelectors'
import {
  outletFiltersOrderFormatSelector,
  outletFiltersServiceTimeSelector,
} from '../../outlet/store/outletSelector'
import { outletSetOrderConfigureFilterModal } from '../../outlet/store/outletSlice'
import { OrderConfigureFilterMode } from '../../outlet/types'
import { preOrderCheckoutSelector } from '../../preOrder/store/preOrderSelectors'
import { AppRoutes } from '../constants'
import getServiceTimeLabel from '../utils/getServiceTimeLabel'

import useGetOrderFormatName from './useGetOrderFormatName'

/**
 * What the first step of the order flow chose ("Takeaway · Today, 12:30")
 * and how to change it. A group order's setup is fixed once it is created,
 * so pre-order checkouts can't be changed from the step bar.
 */
export default function useOrderSetupStep() {
  const { t } = useTranslation('common')
  const dispatch = useAppDispatch()
  const { pathname } = useRouter()
  const getOrderFormatName = useGetOrderFormatName()

  const isCheckoutPage = pathname === AppRoutes.Checkout

  const preOrderCheckout = useAppSelector(preOrderCheckoutSelector)
  const filterOrderFormat = useAppSelector(
    isCheckoutPage
      ? checkoutFiltersOrderFormatSelector
      : outletFiltersOrderFormatSelector,
  )
  const filterServiceTime = useAppSelector(
    isCheckoutPage
      ? checkoutFiltersServiceTimeSelector
      : outletFiltersServiceTimeSelector,
  )

  const orderFormat = preOrderCheckout?.orderFormat ?? filterOrderFormat
  const serviceTime = preOrderCheckout?.serviceTime ?? filterServiceTime

  const formatName = getOrderFormatName(orderFormat)
  const summary = [formatName, getServiceTimeLabel(serviceTime, t)]
    .filter(Boolean)
    .join(' · ')

  const canEdit = !preOrderCheckout

  const edit = useCallback(() => {
    dispatch(
      outletSetOrderConfigureFilterModal(
        OrderConfigureFilterMode.OrderTypeAndTime,
      ),
    )
  }, [dispatch])

  return { summary, canEdit, edit }
}
